import React, { memo } from 'react'
import { FormFieldWrapper } from './FormFieldWrapper'

export interface TextInputFieldProps {
  label: React.ReactNode
  id: string
  name: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onFocus?: React.FocusEventHandler<HTMLInputElement>
  placeholder?: string
  type?: 'text' | 'email' | 'tel'
  maxLength?: number
  disabled?: boolean
  error?: string
  helperText?: string
  showHelper?: boolean
  fieldRef?: React.RefObject<HTMLInputElement>
  wrapperRef?: React.RefObject<HTMLDivElement>
  customStyles?: React.CSSProperties
  /** Extra classes for the error message text only (e.g. a smaller font for a long message in a narrow field). Every other usage is unaffected. */
  errorClassName?: string
  /**
   * Width for the label + error-text wrapper when it should differ from the
   * input's own width (customStyles.width) — e.g. a long error message that
   * needs more room to wrap onto one line than a narrow fixed-width input
   * provides. Falls back to customStyles.width, then to no constraint.
   */
  errorWrapperWidth?: string
}

export const TextInputField = memo<TextInputFieldProps>(({
  label,
  id,
  name,
  value,
  onChange,
  onFocus,
  placeholder = '',
  type = 'text',
  maxLength,
  disabled = false,
  error,
  helperText,
  showHelper,
  fieldRef,
  wrapperRef,
  customStyles,
  errorClassName,
  errorWrapperWidth
}) => (
  <FormFieldWrapper
    label={label}
    id={id}
    error={error}
    helperText={helperText}
    showHelper={showHelper}
    fieldRef={wrapperRef}
    style={
      errorWrapperWidth
        ? { width: errorWrapperWidth }
        : customStyles?.width
          ? { width: customStyles.width }
          : undefined
    }
    errorMessageClassName={errorClassName}
  >
    <div className="w-full relative">
      <input
        ref={fieldRef}
        autoComplete="on"
        className={`w-full min-w-0 block border border-border-light rounded-md p-2 !text-black ${error ? 'border-border-red' : ''} ${disabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
        style={customStyles ? { width: '100%', ...customStyles } : { width: '100%' }}
        id={id}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        disabled={disabled}
        placeholder={disabled ? '' : placeholder}
        maxLength={maxLength}
        aria-describedby={error ? `${id}-error` : undefined}
        aria-invalid={!!error}
      />
    </div>
  </FormFieldWrapper>
))

TextInputField.displayName = 'TextInputField'

export default TextInputField
